import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import {
  Sparkles, FileText, Network, Wand2, Check, X, Plus, ArrowRight,
  Lightbulb, Eye, Scale, MessageCircle, Loader2,
} from "lucide-react";
import { decomposeIdea } from "@/lib/ai-client";
import type { AIDecompositionResult } from "@/types";
import { nodeMeta, provenanceMeta } from "@/lib/vocabulary";
import { ATTACHMENT_META, INTENT_META } from "@/components/flow/StrainNode";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { useToast } from "@/components/Toast";
import { cn } from "@/lib/cn";

type Mode = "paste" | "blank" | "template";

const TEMPLATES = [
  { key: "hot_take", label: "Hot take", icon: "🔥", desc: "A strong, contestable claim worth structuring." },
  { key: "policy", label: "Policy argument", icon: "🏛", desc: "Should we do X? Weighs tradeoffs and criteria." },
  { key: "startup", label: "Startup idea", icon: "🚀", desc: "A thesis about a market, problem, or solution." },
  { key: "hypothesis", label: "Research hypothesis", icon: "🔬", desc: "A testable claim with evidence and scope." },
  { key: "moral", label: "Moral claim", icon: "⚖️", desc: "A normative position with value criteria." },
  { key: "steelman", label: "Steelman", icon: "🛡", desc: "The strongest version of someone else's view." },
];

const LOADING_PHRASES = [
  "finding the main claim…",
  "looking for hidden assumptions…",
  "separating claims from examples…",
  "checking where the idea hinges…",
  "drafting a forkable structure…",
];

const SAMPLE_TEXT = `Honestly I don't think college is worth it for most people anymore. Kids graduate with tens of thousands in debt, half the jobs don't use what they studied, and the kids who went to college were already the smart, ambitious ones anyway. Maybe trades are the better bet for a lot of people.`;

export default function Create() {
  const navigate = useNavigate();
  const toast = useToast((s) => s.push);
  const publishDraft = useStore((s) => s.publishDraft);
  const saveDraft = useStore((s) => s.saveDraft);
  const loginDemo = useStore((s) => s.loginDemo);

  const [mode, setMode] = useState<Mode>("paste");
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [visibility, setVisibility] = useState<"public" | "unlisted" | "private">("public");

  const [loading, setLoading] = useState(false);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [result, setResult] = useState<AIDecompositionResult | null>(null);
  const [rejectedNodes, setRejectedNodes] = useState<Set<string>>(new Set());
  const [rejectedVariants, setRejectedVariants] = useState<Set<number>>(new Set());
  const [editedNodes, setEditedNodes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const phraseTimer = useRef<number | null>(null);

  const runDecompose = async () => {
    if (!text.trim()) {
      toast({ tone: "warn", title: "Paste some text first", desc: "Add your idea prose, then draft a map." });
      return;
    }
    loginDemo().catch(() => undefined); // ensure logged in for the create flow
    setLoading(true);
    setError(null);
    setResult(null);
    setRejectedNodes(new Set());
    setRejectedVariants(new Set());
    setEditedNodes({});

    // cycle loading phrases for the mock-AI feel
    phraseTimer.current = window.setInterval(() => {
      setPhraseIdx((i) => (i + 1) % LOADING_PHRASES.length);
    }, 900);

    try {
      const r = await decomposeIdea({ text });
      setResult(r);
      if (!title) setTitle(r.title);
      if (!summary) setSummary(r.summary);
      toast({ tone: "ok", title: "Map drafted", desc: "Inferred nodes are violet — accept or reject each." });
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong");
      toast({ tone: "error", title: "Decomposition failed", desc: "Try again, or edit manually." });
    } finally {
      if (phraseTimer.current) clearInterval(phraseTimer.current);
      setLoading(false);
    }
  };

  const toggleReject = (tempId: string) => {
    const next = new Set(rejectedNodes);
    if (next.has(tempId)) next.delete(tempId); else next.add(tempId);
    setRejectedNodes(next);
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const publish = async () => {
    if (!result) return;
    const slug = (title || "untitled-strain").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + Date.now().toString(36).slice(-4);

    const filtered: AIDecompositionResult = {
      ...result,
      title: title || result.title,
      summary: summary || result.summary,
      nodes: result.nodes
        .filter((n) => !rejectedNodes.has(n.temp_id))
        .map((n) => ({ ...n, text: editedNodes[n.temp_id] ?? n.text, provenance: n.provenance === "ai_inferred" ? "ai_inferred" : n.provenance })),
      edges: result.edges.filter((e) => !rejectedNodes.has(e.source) && (!e.target || !rejectedNodes.has(e.target))),
      thesis_variants: (result.thesis_variants ?? []).filter((_, i) => !rejectedVariants.has(i)),
    };
    saveDraft(slug, { ...filtered, originalText: text });
    const publishedSlug = await publishDraft(slug, slug, filtered.title, filtered.summary, tags, visibility);
    toast({ tone: "ok", title: "Strain published", desc: "Your strain is live in the feed." });
    navigate(`/s/${publishedSlug}`);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight">Create a strain</h1>
        <p className="text-[13px] text-[var(--color-ink-muted)] mt-0.5">
          Start with prose. We'll draft a structure you can accept, reject, or edit. AI is a proposal, not a verdict.
        </p>
      </div>

      {/* mode picker */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <ModeCard active={mode === "paste"} onClick={() => setMode("paste")} icon={<FileText size={16} />} label="Paste an idea" desc="Prose → structured map" />
        <ModeCard active={mode === "blank"} onClick={() => setMode("blank")} icon={<Network size={16} />} label="Blank canvas" desc="Build it by hand" />
        <ModeCard active={mode === "template"} onClick={() => setMode("template")} icon={<Wand2 size={16} />} label="From template" desc="Start structured" />
      </div>

      {mode === "template" && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-5">
          {TEMPLATES.map((t) => (
            <button
              key={t.key}
              onClick={() => { setText(`${t.label}: `); setTitle(""); setMode("paste"); toast({ tone: "info", title: t.label, desc: t.desc }); }}
              className="text-left rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 hover:border-[var(--color-primary)] transition-colors"
            >
              <div className="text-xl mb-1">{t.icon}</div>
              <div className="text-[13px] font-semibold">{t.label}</div>
              <div className="text-[11px] text-[var(--color-ink-muted)] mt-0.5">{t.desc}</div>
            </button>
          ))}
        </div>
      )}

      {mode === "blank" && (
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center">
          <Network size={32} className="text-[var(--color-ink-faint)] mx-auto mb-3" />
          <h3 className="text-base font-semibold">Blank canvas</h3>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-1 mb-4 max-w-sm mx-auto">
            Start from scratch. You'll add nodes and edges one at a time in the explorer.
          </p>
          <Button onClick={() => {
            const slug = "blank-" + Date.now().toString(36).slice(-4);
            saveDraft("__blank__" + slug, {
              title: title || "Untitled strain",
              summary: summary || "A blank strain.",
              nodes: [],
              edges: [],
              originalText: "",
            });
            loginDemo()
              .then(() => publishDraft("__blank__" + slug, slug, title || "Untitled strain", summary || "A blank strain.", tags, visibility))
              .then((publishedSlug) => {
                toast({ tone: "ok", title: "Blank strain created" });
                navigate(`/s/${publishedSlug}`);
              });
          }} leftIcon={<Plus size={15} />}>Create empty strain</Button>
        </div>
      )}

      {mode !== "blank" && (
        <>
          {/* text input */}
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">your idea (prose)</label>
              <button onClick={() => setText(SAMPLE_TEXT)} className="text-[11px] text-[var(--color-primary)] hover:underline">
                try sample
              </button>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={5}
              placeholder="Write or paste your idea, argument, essay, tweet, or rough thought…"
              className="w-full bg-transparent text-[15px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] outline-none resize-none font-serif leading-relaxed"
            />
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--color-border)]">
              <span className="text-[11px] text-[var(--color-ink-faint)] font-mono">{text.length} chars</span>
              <Button onClick={runDecompose} disabled={loading || !text.trim()} leftIcon={loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}>
                {loading ? "Drafting…" : "Draft a map"}
              </Button>
            </div>
          </div>

          {/* loading state */}
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="rounded-2xl border border-[rgba(167,139,250,0.3)] bg-[rgba(167,139,250,0.05)] p-5 mb-4"
              >
                <div className="flex items-center gap-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <Sparkles size={18} className="text-[var(--color-inferred)]" />
                  </motion.div>
                  <div>
                    <div className="text-[13px] font-medium text-[var(--color-ink)]">AI is drafting your map</div>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={phraseIdx}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        className="text-[12px] text-[var(--color-inferred)] font-mono"
                      >
                        {LOADING_PHRASES[phraseIdx]}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
                {/* skeleton lines */}
                <div className="mt-4 space-y-2">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="h-3 rounded bg-[var(--color-surface-3)]"
                      initial={{ width: "0%" }}
                      animate={{ width: `${60 + i * 12}%` }}
                      transition={{ delay: i * 0.3, duration: 0.8 }}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* error */}
          {error && (
            <div className="rounded-xl border border-[var(--color-danger)] bg-[rgba(244,63,94,0.06)] p-3 mb-4 text-[13px] text-[var(--color-danger)]">
              {error}
            </div>
          )}

          {/* result: suggestion review panel */}
          {result && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4"
            >
              {/* meta fields */}
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 space-y-3">
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">title</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-[14px] text-[var(--color-ink)]" />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">summary</label>
                  <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-2)] text-[13px] text-[var(--color-ink)] resize-none" />
                </div>
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">tags</label>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    {tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-[var(--color-surface-3)] text-[var(--color-ink-soft)]">
                        #{t}
                        <button onClick={() => setTags(tags.filter((x) => x !== t))} className="text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]"><X size={11} /></button>
                      </span>
                    ))}
                    <input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                      placeholder="add tag…"
                      className="bg-transparent text-[12px] text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] outline-none w-24"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">visibility</label>
                  <div className="flex gap-1">
                    {(["public", "unlisted", "private"] as const).map((v) => (
                      <button key={v} onClick={() => setVisibility(v)} className={cn("px-2 py-0.5 rounded text-[11px] border", visibility === v ? "border-[var(--color-primary)] text-[var(--color-primary)]" : "border-[var(--color-border)] text-[var(--color-ink-muted)]")}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* questions for the user */}
              {result.questions_for_user && result.questions_for_user.length > 0 && (
                <div className="rounded-xl border border-[rgba(96,165,250,0.25)] bg-[rgba(96,165,250,0.05)] p-4">
                  <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-define)] mb-2">
                    <MessageCircle size={11} /> questions for you
                  </div>
                  <ul className="space-y-1.5">
                    {result.questions_for_user.map((q, i) => (
                      <li key={i} className="text-[13px] text-[var(--color-ink-soft)] flex items-start gap-2">
                        <span className="text-[var(--color-define)] mt-0.5">?</span> {q}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* nodes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)]">
                    suggested nodes ({result.nodes.filter((n) => !rejectedNodes.has(n.temp_id)).length}/{result.nodes.length})
                  </div>
                  <div className="flex gap-2">
                    <Button size="xs" variant="ghost" onClick={() => setRejectedNodes(new Set())}>Restore all</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {result.nodes.map((n) => {
                    const meta = nodeMeta(n.type);
                    const prov = provenanceMeta(n.provenance);
                    const Icon = meta.icon;
                    const isInferred = n.provenance === "ai_inferred";
                    const rejected = rejectedNodes.has(n.temp_id);
                    const edited = editedNodes[n.temp_id];
                    return (
                      <motion.div
                        key={n.temp_id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: rejected ? 0.4 : 1, y: 0 }}
                        className={cn(
                          "rounded-xl border p-3 transition-all",
                          rejected ? "border-[var(--color-border)] bg-[var(--color-surface-2)]/30" :
                          isInferred ? "border-[rgba(167,139,250,0.3)] bg-[rgba(167,139,250,0.04)]" :
                          "border-[var(--color-border)] bg-[var(--color-surface)]",
                        )}
                      >
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <Badge tone="default" icon={<Icon size={10} />} style={{ color: meta.color }}>{meta.short}</Badge>
                          {n.attachment_kind && (
                            <Badge tone={n.attachment_kind === "exception" ? "inferred" : "muted"}>
                              docks as {n.attachment_kind}
                            </Badge>
                          )}
                          {isInferred && <Badge tone="inferred" icon={<prov.icon size={10} />}>inferred</Badge>}
                          {n.flags?.includes("crux") && <Badge tone="amber" icon={<Scale size={10} />}>crux</Badge>}
                          {n.flags?.includes("needs evidence") && <Badge tone="warn">needs evidence</Badge>}
                          <div className="ml-auto flex gap-1">
                            <button
                              onClick={() => {
                                const next = { ...editedNodes };
                                if (next[n.temp_id]) delete next[n.temp_id]; else next[n.temp_id] = n.text;
                                setEditedNodes(next);
                              }}
                              className="text-[var(--color-ink-faint)] hover:text-[var(--color-ink)] p-1"
                              title="Edit"
                            >
                              ✎
                            </button>
                            <button
                              onClick={() => toggleReject(n.temp_id)}
                              className={cn("p-1", rejected ? "text-[var(--color-support)]" : "text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]")}
                              title={rejected ? "Restore" : "Reject"}
                            >
                              {rejected ? <Check size={13} /> : <X size={13} />}
                            </button>
                          </div>
                        </div>
                        {edited !== undefined ? (
                          <textarea
                            value={edited}
                            onChange={(e) => setEditedNodes({ ...editedNodes, [n.temp_id]: e.target.value })}
                            rows={2}
                            className="w-full bg-transparent text-[13px] text-[var(--color-ink)] outline-none resize-none font-serif"
                          />
                        ) : (
                          <div className="text-[13px] text-[var(--color-ink-soft)] font-serif leading-snug">{n.text}</div>
                        )}
                        {n.original_text && n.original_text !== n.text && (
                          <div className="mt-1 text-[11px] text-[var(--color-ink-faint)] italic font-serif">“{n.original_text}”</div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* proposed thesis wordings — publish as variants, not nodes */}
              {result.thesis_variants && result.thesis_variants.length > 0 && (
                <div className="rounded-xl border border-[rgba(94,234,212,0.25)] bg-[rgba(94,234,212,0.04)] p-4">
                  <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-primary)] mb-1">
                    <Network size={11} /> proposed wordings
                  </div>
                  <p className="text-[11px] text-[var(--color-ink-muted)] mb-2.5">
                    These publish into the thesis's version rail — browsable alternatives, not extra nodes.
                  </p>
                  <div className="space-y-2">
                    {result.thesis_variants.map((v, i) => {
                      const rejected = rejectedVariants.has(i);
                      return (
                        <div key={i} className={cn("rounded-lg border p-2.5 transition-opacity", rejected ? "border-[var(--color-border)] opacity-40" : "border-[var(--color-border)] bg-[var(--color-surface)]")}>
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: INTENT_META[v.intent]?.color }}>
                              {INTENT_META[v.intent]?.label ?? v.intent}
                            </span>
                            <button
                              onClick={() => {
                                const next = new Set(rejectedVariants);
                                if (next.has(i)) next.delete(i); else next.add(i);
                                setRejectedVariants(next);
                              }}
                              className={cn("ml-auto p-1", rejected ? "text-[var(--color-support)]" : "text-[var(--color-ink-faint)] hover:text-[var(--color-danger)]")}
                              title={rejected ? "Restore" : "Reject"}
                            >
                              {rejected ? <Check size={13} /> : <X size={13} />}
                            </button>
                          </div>
                          <div className="text-[13px] text-[var(--color-ink-soft)] font-serif leading-snug">{v.text}</div>
                          {v.note && <div className="mt-1 text-[11px] text-[var(--color-ink-faint)]">{v.note}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* likely cruxes */}
              {result.likely_cruxes && result.likely_cruxes.length > 0 && (
                <div className="rounded-xl border border-[rgba(251,191,36,0.25)] bg-[rgba(251,191,36,0.05)] p-4">
                  <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-secondary)] mb-2">
                    <Scale size={11} /> likely cruxes
                  </div>
                  <ul className="space-y-1.5">
                    {result.likely_cruxes.map((c, i) => {
                      const node = result.nodes.find((n) => n.temp_id === c.node_temp_id);
                      return (
                        <li key={i} className="text-[12px] text-[var(--color-ink-soft)] flex items-start gap-2">
                          <Eye size={12} className="text-[var(--color-secondary)] mt-0.5 shrink-0" /> {c.reason}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* publish */}
              <div className="sticky bottom-4 flex items-center justify-between rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]/90 backdrop-blur p-3 shadow-pop">
                <div className="text-[12px] text-[var(--color-ink-muted)]">
                  <Lightbulb size={12} className="inline mr-1 text-[var(--color-secondary)]" />
                  Inferred nodes stay labeled until accepted in the explorer.
                </div>
                <Button onClick={publish} rightIcon={<ArrowRight size={14} />}>Publish strain</Button>
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}

function ModeCard({ active, onClick, icon, label, desc }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; desc: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "text-left rounded-xl border p-3 transition-all",
        active ? "border-[var(--color-primary)] bg-[var(--color-surface-2)] shadow-glow-primary" : "border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-border-bright)]",
      )}
    >
      <div className={cn("mb-1.5", active ? "text-[var(--color-primary)]" : "text-[var(--color-ink-muted)]")}>{icon}</div>
      <div className="text-[13px] font-semibold">{label}</div>
      <div className="text-[11px] text-[var(--color-ink-muted)] mt-0.5">{desc}</div>
    </button>
  );
}
